<template>
  <div class="quiz" :class="classes">
    <div class="quiz__head">
      <div class="quiz__title" v-html="quiz.title" />
      <div class="quiz__text" v-html="quiz.text" />
    </div>
    <div class="quiz__screen screen">
      <img
        class="screen__img"
        :src="image"
        :width="config.width"
        :height="config.height"
      />

      <div class="quiz__answers" v-if="!(answer && answer.correct)">
        <Answer
          v-for="a in quiz.answers"
          :answer="a"
          :active="answer"
          :key="a.ud"
          :ref="a.id"
          class="quiz__answer"
          @init="checkConnections"
          @click.native="answer = a"
        />
      </div>

      <div class="screen__content">
        <Content
          v-for="content in currentContent"
          :baseWidth="baseWidth"
          :baseHeight="baseHeight"
          :content="content"
          :config="config"
          :key="quiz.id + content.id"
          :ref="content.id"
          @init="checkConnections"
          v-on="$listeners"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Content from "@/components/content";
import Answer from "@/components/quiz-answer";
import arrowCreate from "arrows-svg";

export default {
  components: {
    Content,
    Answer,
  },
  data() {
    return {
      answer: false,
      final: 0,
      loadedContent: [],
      arrows: [],
    };
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    quiz: {
      type: Object,
      required: true,
    },
  },
  computed: {
    classes() {
      return this.quiz.mod ? this.quiz.mod.map((c) => `quiz--${c}`) : [];
    },
    prevAllowed() {
      return !this.answer || this.final === 0;
    },
    nextAllowed() {
      return !this.answer || this.final >= this.answer.final.length - 1;
    },
    baseWidth() {
      return this.quiz?.width || this.config.width;
    },
    baseHeight() {
      return this.quiz?.height || this.config.height;
    },
    currentImage() {
      return this.answer
        ? this.answer.final[this.final].image
        : this.quiz.image;
    },
    image() {
      if (this.currentImage.endsWith("/")) {
        return `${this.config.base}/${this.currentImage}${this.screenWidth}${this.screenPrefix}.jpg`;
      } else {
        return `${this.config.base}/${this.currentImage}`;
      }
    },
    screenPrefix() {
      return window.devicePixelRatio > 1.5 ? "-2x" : "";
    },
    screenWidth() {
      if (this.$vssWidth >= 1200) {
        return "1110";
      }
      if (this.$vssWidth >= 768) {
        return "930";
      }
      return "722";
    },
    currentContent() {
      return this.answer
        ? this.answer.final[this.final].content
        : this.quiz.answers.flatMap((a) => a.content);
    },
  },
  watch: {
    quiz() {
      this.arrows.forEach((a) => {
        a.clear();
      });
      this.arrows = [];
      this.loadedContent = [];
      this.answer = false;
    },
    answer(a) {
      this.arrows.forEach((a) => {
        a.clear();
      });
      this.arrows = [];
      this.loadedContent = this.quiz.answers.map((a) => a.id);

      const e = new window.jQuery.Event("module-quiz", {
        detail: {
          chapter: this.config.id,
          screen: this.quiz.id,
          answer: a.id,
          correct: a.correct || false,
        },
      });
      window.jQuery(document).trigger(e);
    },
    final() {
      this.arrows.forEach((a) => {
        a.clear();
      });
      this.arrows = [];
      this.loadedContent = this.quiz.answers.map((a) => a.id);
    },
  },
  methods: {
    nextFinal() {
      this.final = this.final + 1;
    },
    prevFinal() {
      this.final = this.final - 1;
    },
    checkConnections(id) {
      this.loadedContent.push(id);

      if (
        this.loadedContent.length ===
        this.currentContent.length + this.quiz.answers.length
      ) {
        this.currentContent.forEach((c) => {
          if (c.connect && this.$refs[c.connect.element].length) {
            const arrow = arrowCreate({
              from: {
                node: this.$refs[c.id][0].$el,
                direction: c.connect?.from?.direction,
                translation: c.connect?.from?.translation || null,
              },
              to: {
                node: this.$refs[c.connect.element][0].$el,
                direction: c.connect?.to?.direction,
                translation: c.connect?.to?.translation || null,
              },
            });

            this.arrows.push(arrow);
            document.body.appendChild(arrow.node);
          }
        });

        this.quiz.answers.forEach((a) => {
          if (a.connect && this.$refs[a.connect.element].length) {
            const arrow = arrowCreate({
              from: {
                node: this.$refs[a.id][0].$el,
                direction: a.connect?.from?.direction,
                translation: a.connect?.from?.translation || null,
              },
              to: {
                node: this.$refs[a.connect.element][0].$el,
                direction: a.connect?.to?.direction,
                translation: a.connect?.to?.translation || null,
              },
            });

            this.arrows.push(arrow);
            document.body.appendChild(arrow.node);
          }
        });
      }
    },
  },
  beforeDestroy() {
    this.arrows.forEach((a) => {
      a.clear();
    });
  },
};
</script>

<style lang="scss" scoped>
.quiz {
  &__head {
    background: #f6f6fa;
    margin: 0 0 1rem;
    padding: 2rem 1rem;
  }
  &__title {
    text-align: center;
    font-size: 1.5rem;
    color: #292983;
    font-weight: bold;
    text-transform: uppercase;
  }
  &__text {
    text-align: center;
    margin: 1rem 0 0;
  }

  &__answers {
    position: absolute;
    bottom: 2.5rem;
    left: 2.5rem;
    right: 2.5rem;
    display: flex;
    align-items: stretch;
    justify-content: center;
    z-index: 2;

    @include media-breakpoint-down(xl) {
      bottom: 1.5rem;
      left: 1.5rem;
      right: 1.5rem;
    }

    @include media-breakpoint-down(lg) {
      bottom: 1rem;
      left: 1rem;
      right: 1rem;
    }
  }

  &--shaded {
    .screen {
      &:before {
        display: none;
      }
    }
  }
  &--answers-top &__answers {
    bottom: auto;
    top: 2.5rem;

    @include media-breakpoint-down(xl) {
      top: 1.5rem;
    }

    @include media-breakpoint-down(lg) {
      top: 1rem;
    }
  }
}
.screen {
  position: relative;
  border-top: 10px solid #ffd100;
  border-bottom: 10px solid #ffd100;

  &__img {
    width: 100%;
    height: auto;
  }

  &:before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(#868686, 0.4);
  }
  &__content {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }
}
</style>
